import { TextField, TextFieldProps } from "@mui/material";

import { Control, Controller, FieldValues, Path } from "react-hook-form";
import FieldGridContainer from "../fieldGridContainer";

export type ControlledTextFieldProps<T extends FieldValues = FieldValues> =
  TextFieldProps & {
    control?: Control<T>;
    isGridItem?: boolean;
    label: string;
    id: string;
    name: Path<T>;
    errorMessage?: string;
  };

/**
 * RHF's Control<T> is invariant; bridging here avoids `as any` at every call site.
 */
function ControlledTextField<T extends FieldValues = FieldValues>(
  props: ControlledTextFieldProps<T>,
) {
  const {
    control,
    isGridItem = true,
    label,
    id,
    name,
    helperText = "",
    errorMessage,
    multiline,
    InputLabelProps,
    fullWidth,
    ...otherProps
  } = props;

  const resolvedControl = control as unknown as Control<FieldValues>;
  const resolvedName = name as string;

  const isMultiline = multiline === true;
  const mergedInputLabelProps = isMultiline
    ? { shrink: true as const, ...InputLabelProps }
    : InputLabelProps;

  if (isGridItem) {
    return (
      <FieldGridContainer data-testid={`${id}-container`}>
        <Controller
          name={resolvedName}
          control={resolvedControl}
          render={({ field, fieldState }) => {
            const validationMessage =
              errorMessage ?? fieldState.error?.message;
            return (
              <TextField
                {...field}
                value={field.value ?? ""}
                id={id}
                label={label}
                size="small"
                variant="outlined"
                helperText={validationMessage ?? helperText}
                error={!!validationMessage}
                multiline={multiline}
                fullWidth={fullWidth}
                InputLabelProps={mergedInputLabelProps}
                {...otherProps}
              />
            );
          }}
        />
      </FieldGridContainer>
    );
  }

  return (
    <Controller
      name={resolvedName}
      control={resolvedControl}
      render={({ field, fieldState }) => {
        const validationMessage = errorMessage ?? fieldState.error?.message;
        return (
          <TextField
            {...field}
            value={field.value ?? ""}
            id={id}
            label={label}
            size="small"
            variant="outlined"
            helperText={validationMessage ?? helperText}
            error={!!validationMessage}
            multiline={multiline}
            fullWidth={fullWidth}
            InputLabelProps={mergedInputLabelProps}
            {...otherProps}
          />
        );
      }}
    />
  );
}

export default ControlledTextField;
